import axios from 'axios';

// In-memory token storage — never touches localStorage or sessionStorage.
// The server sets an httpOnly cookie on login, which the browser sends automatically.
// This module-level variable holds the token only while the tab is open.
let _token = null;

export const setAuthToken = (token) => { _token = token; };
export const clearAuthToken = () => { _token = null; };

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || ''}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send httpOnly cookies with every request
});

// Attach in-memory token as Authorization header when available.
// Falls back to cookie-only auth (e.g., after a page refresh before /auth/me restores memory).
api.interceptors.request.use((config) => {
  if (_token) {
    config.headers.Authorization = `Bearer ${_token}`;
  }
  return config;
});

// Handle 401 errors — clear memory token; cookie is cleared by /auth/logout
// Skip redirect for /auth/me: AuthContext handles unauthenticated state directly,
// avoiding a redirect loop on initial page load.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/me')) {
        clearAuthToken();
        const isMobile = window.location.pathname.startsWith('/m');
        window.location.href = isMobile ? '/m/login' : '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password, rememberMe = false) => api.post('/auth/login', { email, password, remember_me: rememberMe });
// register() helper removed with POST /auth/register (PROMPT 52) — onboarding is invitation-only.
export const getMe = () => api.get('/auth/me');
export const logoutApi = () => api.post('/auth/logout');
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const validateResetToken = (token) => api.get(`/auth/validate-reset-token/${token}`);
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });

// Companies
export const getCompanies = () => api.get('/companies');
export const createCompany = (data) => api.post('/companies', data);

// Users
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);

// Projects
export const getProjects = () => api.get('/projects');
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const getProjectUsers = (projectId) => api.get(`/projects/${projectId}/users`);
export const updateProjectSettings = (data) => api.put('/project-settings', data);
// LC15/2 — supervision by an explicit act. A Company QA Admin no longer reaches
// every project of the tenant by title; these two are how they see and take
// membership of one. Both are no-ops (empty list / 403) for every other role.
export const getJoinableProjects = () => api.get('/projects/joinable');
export const joinProject = (projectId) => api.post(`/projects/${projectId}/join`);

// ============================================================================
// Project Companies (Phase 2 epic CO-1+) — project-scoped legal entities
// ============================================================================
// Distinct from getCompanies() / createCompany() (existing — SaaS tenants).
// These helpers operate on db.project_companies: project-scoped legal entities
// (Subcontractor, MainContractor, Client, Consultant, Supplier, Other) used
// for NCR responsibility attribution and ISO 9001 reporting.
// ============================================================================

// v1.4 regra 2/4 — the caller's OWN access to a project, including whether they
// govern QWPs and which participating company they are. `governs_qwps` is the
// same answer `qwp_authority` gives the write guards; `null` means the server
// has not answered, which is NOT the same as `false`.
// v1.4 regra 3 — revoke/restore a person's access to ONE project. The row and
// the whole history survive; only the ability to enter is withdrawn.
export const revokeProjectAccess = (projectId, userId, reason = null) =>
  api.post(`/projects/${projectId}/access/${userId}/revoke`, { reason });

export const restoreProjectAccess = (projectId, userId) =>
  api.post(`/projects/${projectId}/access/${userId}/restore`);

export const getMyProjectAccess = (projectId) =>
  api.get(`/projects/${projectId}/my-access`);

// ── NL2 — SIGNING AUTHORITY ────────────────────────────────────────────────
//
// ONE door for the WHOLE authority state of one person on one project. The
// body is the desired END state, never a delta: the server applies the
// cascade (no authority => no capacities) itself, so a screen that forgot to
// clear the chips cannot leave the impossible row behind. NL1-D's POST/DELETE
// pair no longer exists.
//
// 422 carries `detail.code` — SIGNING_AUTHORITY_NOT_APPLICABLE,
// CAPACITIES_REQUIRE_SIGNING_AUTHORITY, SIGNING_CAPACITY_NOT_FOUND,
// SIGNING_CAPACITY_INACTIVE, TOO_MANY_SIGNING_CAPACITIES — so a caller
// matches on the code, never on the English.
export const setSigningAuthority = (projectId, userId, body) =>
  api.put(`/projects/${projectId}/users/${userId}/signing-authority`, {
    authorised_signatory: !!body.authorised_signatory,
    capacity_ids: body.capacity_ids || [],
  }).then(r => r.data);

// NL21-A10 / AS-FE — the SAME door, speaking its generalised contract: the body
// carries ONLY what the caller changes, and nothing is defaulted here. A key the
// caller did not pass is not in the body, and the server leaves that authority
// exactly as it is.
//
//   authorised_signatory?  bool
//   capacity_ids?          [id]  — only alongside authorised_signatory; with
//                                  authorised_signatory: true, omitting it CLEARS
//                                  the capacities (the server reads it as the
//                                  end state of that authority)
//   qmp_signatory?         bool
//   reason?                str   — required (1..500) when either authority goes
//                                  from true to false
//
// Refusals are named in `detail.code` (SIGNING_AUTHORITY_ERROR in
// lib/signingAuthority); the sentence a person reads is `detail.message` (see
// lib/errorDetail).
export const updateSigningAuthority = (projectId, userId, body = {}) => {
  const payload = {};
  ['authorised_signatory', 'capacity_ids', 'qmp_signatory', 'reason'].forEach((key) => {
    if (body[key] !== undefined) payload[key] = body[key];
  });
  return api.put(`/projects/${projectId}/users/${userId}/signing-authority`, payload)
    .then(r => r.data);
};

// NL21-A10.5 — the readable role matrix, generated on the server from the
// permission table: [{role, label, card, summary, can: [str], cannot: [str]}],
// five rows in the invite modal's order. `card` is the grid's one-line legend,
// `summary` the panel's / the ⓘ's sentence (the same string for every role but
// the Handover Reviewer). The screen prints what this serves and nothing else.
export const getProjectRoles = (projectId) =>
  api.get(`/projects/${projectId}/roles`).then(r => r.data);

// The project's capacity catalogue. `includeRetired` is what lets a screen
// RESOLVE a capacity somebody still holds after it was retired — the name has
// to render, or a chip would show an id.
export const getSigningCapacities = (projectId, { includeRetired = false } = {}) =>
  api.get(`/projects/${projectId}/signing-capacities`, {
    params: { include_retired: includeRetired },
  }).then(r => r.data);

// Create-or-reuse on the case-insensitive name. 409 with
// `detail.code === 'CAPACITY_RETIRED'` means a retired capacity holds that
// name and reviving it is a deliberate act, not a side effect of typing.
export const createSigningCapacity = (projectId, name) =>
  api.post(`/projects/${projectId}/signing-capacities`, { name }).then(r => r.data);

// Corrects the LABEL only — the id never moves, so nothing re-points.
export const renameSigningCapacity = (projectId, capacityId, name) =>
  api.patch(`/projects/${projectId}/signing-capacities/${capacityId}`, { name })
    .then(r => r.data);

// "Deactivate" in the product; `retired` in the data. Forward-only and NOT a
// delete: existing records keep resolving it by id.
export const retireSigningCapacity = (projectId, capacityId) =>
  api.post(`/projects/${projectId}/signing-capacities/${capacityId}/retire`)
    .then(r => r.data);

export const getProjectCompanies = (projectId, params = {}) => {
  const query = new URLSearchParams();
  if (params.type) query.set('type', params.type);
  if (params.search) query.set('search', params.search);
  if (params.include_deleted) query.set('include_deleted', 'true');
  const qs = query.toString();
  return api.get(`/projects/${projectId}/project-companies${qs ? '?' + qs : ''}`).then(r => r.data);
};

export const getProjectCompany = (projectCompanyId) =>
  api.get(`/project-companies/${projectCompanyId}`).then(r => r.data);

export const createProjectCompany = (projectId, data) =>
  api.post(`/projects/${projectId}/project-companies`, data).then(r => r.data);

// Bloco 3 — per-project asset tag catalogue (descriptive classification).
export const getAssetTags = (projectId) =>
  api.get(`/projects/${projectId}/asset-tags`).then(r => r.data);
export const createAssetTag = (projectId, name) =>
  api.post(`/projects/${projectId}/asset-tags`, { name }).then(r => r.data);

export const updateProjectCompany = (projectCompanyId, data) =>
  api.patch(`/project-companies/${projectCompanyId}`, data).then(r => r.data);

export const deleteProjectCompany = (projectCompanyId) =>
  api.delete(`/project-companies/${projectCompanyId}`).then(r => r.data);

// CO-5: Self-service or admin set/clear of a user's default ProjectCompany.
// Backend: PATCH /api/users/{user_id}/default-project-company
// Body: { project_company_id: string | null } — null clears, string sets.
// Permission: self-update OR platform_owner / project_admin within tenant.
export const updateUserDefaultProjectCompany = (userId, projectCompanyId) =>
  api.patch(`/users/${userId}/default-project-company`, {
    project_company_id: projectCompanyId,
  }).then(r => r.data);

// CO-7.6: Set or clear a per-project Company override for a user.
// Backend: PATCH /api/users/{user_id}/project-company-overrides (CO-7.5)
// projectCompanyId === null clears the override for that project_id.
export const updateUserProjectCompanyOverride = (userId, projectId, projectCompanyId) =>
  api.patch(`/users/${userId}/project-company-overrides`, {
    project_id: projectId,
    project_company_id: projectCompanyId,
  }).then(r => r.data);

// Drawings
export const getDrawings = (projectId) => api.get('/drawings', { params: { project_id: projectId } });
export const getDrawing = (id) => api.get(`/drawings/${id}`);
// PERF-BE-1 — the lot map in ONE read: drawing, project, elements, pins, lots
// (each with its live ITC), the referenced asset groups and per-asset status.
// Contract: docs/design/lot-map.md.
export const getDrawingMap = (drawingId) => api.get(`/drawings/${drawingId}/map`);
export const createDrawing = (data) => api.post('/drawings', data);
export const uploadDrawingFile = (drawingId, file, pageNumber = 1) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('page_number', pageNumber);
  return api.post(`/drawings/${drawingId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const changeDrawingPage = (drawingId, pageNumber) => {
  return api.post(`/drawings/${drawingId}/change-page`, null, { params: { page_number: pageNumber } });
};
export const deleteDrawing = (id) => api.delete(`/drawings/${id}`);
export const updateDrawing = (id, data) => api.patch(`/drawings/${id}`, data);
export const uploadDrawingRevision = (drawingId, file, pageNumber = 1) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('page_number', pageNumber);
  return api.post(`/drawings/${drawingId}/upload-revision`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Asset Groups / ITPs
export const getAssetGroups = (projectId) => api.get('/asset-groups', { params: { project_id: projectId } });
export const getAssetGroup = (id) => api.get(`/asset-groups/${id}`);
export const createAssetGroup = (data) => api.post('/asset-groups', data);
export const updateAssetGroup = (id, data) => api.put(`/asset-groups/${id}`, data);
export const deleteAssetGroup = (id) => api.delete(`/asset-groups/${id}`);
export const getAssetGroupAssets = (id) => api.get(`/asset-groups/${id}/assets`);

// ----- Quality Work Packages (QWP v1.3) -----
// The hierarchy the product now shows is Project -> QWP -> Asset Group -> Asset.
// There is no "Quality Breakdown" entity in the UI any more; the bench screen is
// the QWP Page, addressed by the package.
//
// Each row of the LIST carries `owner_company_name`, `asset_group_count` and the
// DERIVED `status` (setup | active | closed) — none of them fields of the entity,
// all of them things the list has to show and cannot compute client-side.
export const listQwps = (projectId) => api.get(`/projects/${projectId}/qwps`);

// GOVERNANCE — the owning company's alone (v1.3 regra 1). A participant admin
// gets a 403 from every one of these; the UI simply does not render the controls,
// and the backend refuses regardless of what the UI renders.
export const createQwp = (projectId, data) =>
  api.post(`/projects/${projectId}/qwps`, data);
export const updateQwp = (qwpId, data) => api.patch(`/qwps/${qwpId}`, data);
export const deleteQwp = (qwpId) => api.delete(`/qwps/${qwpId}`);

// QMP-FE-1 — the package's LIVE structure (NL21-A1). The PUT carries BOTH
// positions every time (null = "None"): the server refuses a body that omits
// one, because an omitted key would otherwise clear it in silence. A lost race
// answers 409 `detail.error = "QWP_STRUCTURE_CHANGED"`.
export const updateQwpParties = (qwpId, body) =>
  api.put(`/qwps/${qwpId}/parties`, {
    verification_company_id: body.verification_company_id ?? null,
    acceptance_company_id: body.acceptance_company_id ?? null,
  });
// Names the Executing Party of a package born "To be appointed" — ONCE. A
// named executor changes by transfer (`updateQwp` with `owner_company_id`).
export const appointQwpExecutingParty = (qwpId, participationId) =>
  api.post(`/qwps/${qwpId}/executing-party/appoint`,
    { participation_id: participationId });
export const reparentAssetGroup = (assetGroupId, qwpId) =>
  api.post(`/asset-groups/${assetGroupId}/qwp`, { qwp_id: qwpId });

// THE PAGE. One read: the package, its official structure (groups in order, each
// with its assets in order and their lifecycle), and the pendings of the open
// generation. READ-ONLY — `open_generation` is null until somebody writes.
export const getQwpPage = (qwpId) => api.get(`/qwps/${qwpId}/page`);

// GET-OR-CREATE the open generation. Called at the first act of WRITING, never on
// mount: a mount-time call would have a client representative minting a bench by
// looking at the screen. Returns { generation, created }.
export const openQwpGeneration = (qwpId) =>
  api.post(`/qwps/${qwpId}/generations/open`);

// ORDER — presentation only. Neither call can move a group to another package or
// an asset to another group: the backend refuses any id that is not already a
// member of the list being ordered.
export const reorderQwpAssetGroups = (qwpId, assetGroupIds) =>
  api.post(`/qwps/${qwpId}/asset-groups/order`, { asset_group_ids: assetGroupIds });
export const reorderAssetGroupAssets = (assetGroupId, assetIds) =>
  api.post(`/asset-groups/${assetGroupId}/assets/order`, { asset_ids: assetIds });

// ----- MOVING ASSETS BETWEEN PACKAGES (SA-2b-3 backend / SA-2c screen) -----
//
// Three doors, one subject: a set of assets of THIS package, and the package
// they should belong to instead. `project_id` is never in a body here — the
// server resolves it from the SOURCE package and refuses any asset it cannot
// reach from that package's own groups (the endpoint's anti-IDOR).

// The QUALITY RECORDS column, and the flag that decides whether an asset can be
// picked at all. Answers for EVERY asset of the package — a clean asset comes
// back as {summary: "", has_records: false, booked: false, counts: {}} rather
// than being absent, so a missing key means "the package does not contain this
// asset", never "this asset is clean". Its fence is the one `GET /qwps/{id}/page`
// uses, so anyone who can read the table can read this.
export const getQwpAssetRecords = (qwpId) =>
  api.get(`/qwps/${qwpId}/asset-records`);

// WHAT WOULD HAPPEN, without anything happening: no write, and no asset number
// consumed. Returns `state` (move | scope_transfer | blocked_booked — SA-3a
// retired blocked_records from this door) and, per Asset Group, the numbers
// the dialog's sentences are written from. The server sends NUMBERS, not
// sentences — the one exception is `family_conflict.message`, which is a
// refusal rather than a description. A selection carrying records also gets
// the §3b numbers in `totals` (signed/open/NCRs/users/A2).
export const previewQwpMove = (qwpId, { asset_ids, target_qwp_id }) =>
  api.post(`/qwps/${qwpId}/move/preview`, { asset_ids, target_qwp_id });

// THE MOVE. Same body and the same gates as the preview, in ONE transaction
// across every Asset Group involved — all or nothing — with one activity-log
// entry per group sharing an `operation_id`. The response carries `toast`, the
// sentence the screen shows verbatim. SA-3c — `transfer_reason` rides along
// ONLY when the caller has one: the clean move's body stays byte-identical
// (and the server answers 422 to a reason nothing needs).
export const moveQwpAssets = (qwpId, { asset_ids, target_qwp_id, transfer_reason }) =>
  api.post(`/qwps/${qwpId}/move`, {
    asset_ids,
    target_qwp_id,
    ...(transfer_reason ? { transfer_reason } : {}),
  });

// SA-3c — what transferring the WHOLE package to this company would do.
// No write. Same `state` vocabulary as the Move preview, `totals` over every
// asset of the package — no per-group plan, because a whole-QWP transfer
// moves no asset between groups.
export const previewQwpTransfer = (qwpId, { target_company_id }) =>
  api.post(`/qwps/${qwpId}/transfer/preview`, { target_company_id });

// Elements
export const getElements = (params) => api.get('/elements', { params });
export const getElement = (id) => api.get(`/elements/${id}`);
export const createElement = (data) => api.post('/elements', data);
export const deleteElement = (id) => api.delete(`/elements/${id}`);
export const patchElement = (id, data) => api.patch(`/elements/${id}`, data);
// p128-3 — put an asset that was born with no canvas onto an Asset Map. Its own
// route rather than a patchElement field: `drawing_id` stays stripped from PATCH.
export const attachElementToDrawing = (id, drawingId) =>
  api.post(`/elements/${id}/drawing`, { drawing_id: drawingId });

// Element Pins
export const getElementPins = (drawingId) => api.get('/element-pins', { params: { drawing_id: drawingId } });
export const createElementPin = (data) => api.post('/element-pins', data);
export const updateElementPin = (id, data) => api.put(`/element-pins/${id}`, data);
export const deleteElementPin = (id) => api.delete(`/element-pins/${id}`);

// Checklists / Checklist Items helpers DELETED (PROMPT 61 / N1) together with
// the backend route family. All five exports were import-free across the app.

// Inspections — the LEGACY `/inspections*` wrappers are GONE (NL22-A).
//
// `getInspectionsLite` / `getInspection` / `createInspection` /
// `rescheduleInspection` / `cancelInspection` / `getInspectionResponses` /
// `createInspectionResponse` / `getInspectionHistory` existed only for
// `NewInspectionPage` / `InspectionListPage` / `InspectionPage`, all three
// removed in this same slice. The living booking surface is
// `getMyInspectionBookings` and the `/inspection-bookings/*` family below.

// Additional Records
// F4.5 — asset Records WINDOW: aggregated ITC line-item evidence
// (structured_itc_evidences reached via the asset's structured ITCs) UNIONED
// with legacy additional_records. Read-only. Returns { records, legacy }.
export const getAssetRecordsWindow = (assetId, { limit } = {}) =>
  api.get(`/assets/${assetId}/records-window`, { params: limit ? { limit } : {} });

// Inspection Items - /inspection-items endpoints removed (PROMPT 54B, orphan); helpers deleted.

// Photos
export const getElementPhotos = (elementId) => api.get('/photos', { params: { element_id: elementId } });
export const uploadElementPhoto = (elementId, file, lotId = null) => {
  const formData = new FormData();
  formData.append('element_id', elementId);
  formData.append('file', file);
  if (lotId) formData.append('lot_id', lotId);
  return api.post('/photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deletePhoto = (id) => api.delete(`/photos/${id}`);

// Non-Conformities — the legacy-shaped read of the NCR threads (the asset and
// blocker pickers). The NCR itself is read and acted on through the NCR-2
// doors below (`getNcr`, `raiseNcr`, ...).
export const getNCRsByElement = (elementId) => api.get('/non-conformities', { params: { element_id: elementId } });

// FE-7 — the same route, scoped to a PROJECT. `GET /non-conformities` has
// always accepted `project_id`; only the client never asked. The blocker
// picker needs every NCR of the project, not the ones of one asset, because a
// line's blocker may be explained by a non-conformity raised anywhere on it.
//
// The asset helper above is untouched: it has its own callers and its own
// meaning.
//
// WHAT COMES BACK IS THE LEGACY SHAPE (`_list_ncr_threads_as_legacy_shape`),
// `created_at` DESC, and its `status` is TITLE-CASE ("Open", "Closed") while
// the release condition on the server reads the thread's own lowercase
// `"closed"`. A screen that decided anything from this field would be deciding
// from a different vocabulary than the one that governs — so it is SHOWN and
// never read. `id` is the thread id, which is exactly the `ncr_thread_id` the
// link door wants.
export const listProjectNCRs = (projectId) =>
  api.get('/non-conformities', { params: { project_id: projectId } });

// ============================================================================
// NCR-2 — the NCR by company (docs/design/ncr-v1-product-contract.md, D19).
// Every read is the server's whole answer for THIS requester (gates, steps,
// who is expected to do what); every write is one door. The old writes
// (POST /threads/ncr, PATCH /threads/{id}/status, /ncr-investigation) answer
// 410 and have no caller here any more.
// ============================================================================

export const listProjectNcrs = (projectId) =>
  api.get(`/projects/${projectId}/ncrs`).then(r => r.data);
export const getNcr = (ncrId) => api.get(`/ncrs/${ncrId}`).then(r => r.data);
export const getNcrRaiseContext = (elementId) =>
  api.get(`/elements/${elementId}/ncr-raise-context`).then(r => r.data);
export const raiseNcr = (body) => api.post('/ncrs', body).then(r => r.data);

const _ncrUpload = (url, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
// Files are staged by their uploader and enter the record only with the act
// that names them (the raise, a corrective action, a close-out, a make safe).
export const uploadNcrRaiseFile = (elementId, file) =>
  _ncrUpload(`/elements/${elementId}/ncr-files`, file);
export const uploadNcrFile = (ncrId, file) => _ncrUpload(`/ncrs/${ncrId}/files`, file);

const _ncrAct = (ncrId, path, body) =>
  api.post(`/ncrs/${ncrId}/${path}`, body || {}).then(r => r.data);
export const submitNcrCorrectiveAction = (ncrId, body) =>
  _ncrAct(ncrId, 'corrective-actions', body);
export const submitNcrCloseOut = (ncrId, body) => _ncrAct(ncrId, 'close-outs', body);
// family: 'corrective-actions' | 'close-outs' | 'target-requests'
export const decideNcr = (ncrId, family, version, body) =>
  _ncrAct(ncrId, `${family}/${version}/decisions`, body);
export const requestNcrTargetDate = (ncrId, body) => _ncrAct(ncrId, 'target-requests', body);
export const reassignNcr = (ncrId, body) => _ncrAct(ncrId, 'reassign', body);
export const changeNcrAcceptance = (ncrId, body) => _ncrAct(ncrId, 'acceptance-parties', body);
export const amendNcr = (ncrId, body) => _ncrAct(ncrId, 'amendments', body);
export const cancelNcr = (ncrId, body) => _ncrAct(ncrId, 'cancel', body);
export const disputeNcr = (ncrId, body) => _ncrAct(ncrId, 'dispute', body);
export const withdrawNcrDispute = (ncrId, body) => _ncrAct(ncrId, 'dispute/withdraw', body);
export const keepNcrAssignment = (ncrId, body) =>
  _ncrAct(ncrId, 'dispute/keep-assignment', body);
export const recordNcrMakeSafe = (ncrId, body) => _ncrAct(ncrId, 'make-safe', body);

const _SEVERITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
export const formatSeverity = (severity) => {
  if (!severity) return '';
  return _SEVERITY_LABELS[severity.toLowerCase()] || severity;
};

const _NCR_STATUS_LABELS = {
  open: 'Open', under_review: 'Under Review', in_progress: 'In Progress',
  answered: 'Answered', closed: 'Closed',
};
export const formatNCRStatus = (status) => {
  if (!status) return '';
  return _NCR_STATUS_LABELS[status.toLowerCase()] || status;
};

// ============================================================================
// NCR (Phase 1C) — PDF export of the NCR thread (C-20)
// ============================================================================

export const exportThreadPDF = (threadId) =>
  api.get(`/threads/${threadId}/export/pdf`, { responseType: 'blob' });

// Signatures  (getSignatures list helper removed - orphan GET /signatures, PROMPT 54B)
export const createSignature = (data) => api.post('/signatures', data);

// PROMPT 95 — `declareAssetQaComplete` is GONE with human gate 1: QA Complete
// is derived by the server (matriz B) and no endpoint accepts a declaration.

/* ---------------------------------------------------------------------------
 * LC5 / LC6 / LC7 — the handover inspection and the punch list.
 *
 * Every one of these returns `r.data` rather than the older `api.get(...)`
 * helpers that hand back the whole axios response. The two conventions coexist
 * in this file already; a new feature picks one and keeps it, so its callers
 * never have to remember which shape a given helper speaks.
 *
 * `project_id` is NEVER in a POST body — the backend resolves it server-side
 * from the parent (an asset, an inspection) and refuses a body that carries it
 * at all (`extra="forbid"`). It appears only as a query parameter on the two
 * LIST endpoints, where the project IS the thing being listed.
 * ------------------------------------------------------------------------ */

// --- handover inspections -------------------------------------------------

/** Schedule an inspection over a SET of assets. `title` is required by the
 *  server (LC7): a project runs many of these and "3 Sep, 12 assets" is not a
 *  name. Every asset must already derive `qa_complete`; a refusal
 *  names each one and the state it is actually in. */
/**
 * PROMPT 108 — BOOKING A HANDOVER INSPECTION.
 *
 * THIS SIGNATURE IS A SECOND SOURCE OF TRUTH, and it silently disagreed with the
 * form for two batches. PROMPT 99 (§3/§7) added `description` and the invited
 * participants to the modal AND to the server, and never came back here. The
 * destructuring listed neither, so JavaScript did what JavaScript does with keys
 * nobody reads: it dropped them. The POST went out with no description, the
 * server refused it with `'description' is required`, and the toast said that
 * while the field sat filled in on the screen — because the field WAS filled in
 * on the screen, and empty on the wire.
 *
 * The participants were worse, because losing them was silent: the server takes
 * `invited_user_ids` as an optional list, so a booking that got past the
 * description would have been written WITH NOBODY INVITED and no error at all.
 *
 * So every field the form collects is named here, explicitly, and the fix is to
 * carry them — not to relax the server, whose demand for a description is
 * correct and was never the problem.
 *
 * `notes` stays: it is a real optional field on the server and this signature is
 * the only place that has ever named it.
 */
export const createHandoverInspection = (
  {
    element_ids, title, description,
    scheduled_for = null, scheduled_time = null,
    invited_user_ids = [], notes = null,
    // HC-2 — the RECEIVING company, chosen in the modal. `null` is a real
    // answer ("To be assigned"), so it defaults to null and is always sent:
    // dropping the key would be a different request from "nobody yet".
    // There is deliberately no `handover_from_*` here — the executing party is
    // derived server-side from the assets' work packages, and the server
    // refuses a body that tries to state one.
    handover_to_project_company_id = null,
  },
) => api.post('/handover-inspections', {
  element_ids, title, description, scheduled_for, scheduled_time,
  invited_user_ids, notes, handover_to_project_company_id,
}).then(r => r.data);

/**
 * HC-2 — WHO WOULD THIS BOOKING BE FROM, AND WHO WOULD IT SENSIBLY BE TO.
 *
 * Read-only and stored nowhere: the modal sends the `to` a person actually
 * chose back on the create, even when they accepted what was offered.
 *
 * The ids go on as REPEATED query parameters, built here rather than handed to
 * axios: its default array serializer emits `element_ids[]=…`, which FastAPI
 * reads as a different parameter name and answers with an empty list.
 */
export const getHandoverPartiesSuggestion = (elementIds = []) => {
  const query = new URLSearchParams();
  (elementIds || []).forEach((id) => { if (id) query.append('element_ids', id); });
  return api
    .get(`/handover-inspections/parties-suggestion?${query.toString()}`)
    .then(r => r.data);
};

export const listHandoverInspections = (projectId) =>
  api.get('/handover-inspections', { params: { project_id: projectId } })
    .then(r => r.data);

/** H-FE-1 — ONE inspection, as the page draws it: attendees (full rows),
 *  `completion {requested, completed, blockers[], held}`, `actions`, and the
 *  assets with their milestones and punch items. 404 outside the fence. */
export const getHandoverInspection = (inspectionId) =>
  api.get(`/handover-inspections/${inspectionId}`).then(r => r.data);

/** The minimum view a PRESENT attendee outside the fence reads to sign. */
export const getHandoverInspectionAttendance = (inspectionId) =>
  api.get(`/handover-inspections/${inspectionId}/attendance`).then(r => r.data);

/** Organiser: record a person (`{user_id, present}`) or an external attendee
 *  (`{external: {name, company}, present: true}`) on the attendance list. */
export const addHandoverAttendee = (inspectionId, body) =>
  api.post(`/handover-inspections/${inspectionId}/attendees`, body).then(r => r.data);

/** Organiser: change a recorded line (`{present, reason}`); a reason is owed
 *  once the signatures have started. */
export const updateHandoverAttendee = (inspectionId, attendeeId, body) =>
  api.patch(`/handover-inspections/${inspectionId}/attendees/${attendeeId}`, body)
    .then(r => r.data);

/** H-FE-2 B1 — the person's OWN door (H-BE-GAPS-1 B1): no attendee id, no
 *  body. Signing declares presence, so an invitee nobody has recorded signs
 *  here too. Answers with the minimum view (`GET .../attendance`). */
export const signMyHandoverAttendance = (inspectionId) =>
  api.post(`/handover-inspections/${inspectionId}/attendance/sign`).then(r => r.data);

/** Organiser: a present attendee who did not sign, with the reason. */
export const recordHandoverDidNotSign = (inspectionId, attendeeId, reason) =>
  api.post(`/handover-inspections/${inspectionId}/attendees/${attendeeId}/did-not-sign`,
    { reason }).then(r => r.data);

/** The act that MOVES every asset on the event out of
 *  `qa_complete`. Zero punch items is a valid completion.
 *  H-FE-2 B2 — `body` (optional): `{outcomes: [{user_id, outcome:
 *  "did_not_attend" | "did_not_sign", reason}]}`, one per invitee still
 *  `invited`; the request closes their attendance and signs the organiser. */
export const completeHandoverInspection = (inspectionId, body) =>
  api.post(`/handover-inspections/${inspectionId}/complete`, body).then(r => r.data);

// HC-5b — nomear o receptor de uma inspecao JA AGENDADA. O caminho que faltava:
// antes dele, uma HI sem receptor so saia cancelando e reagendando, perdendo a
// data, os convidados e o numero.
export const setHandoverInspectionReceiver = (inspectionId, toProjectCompanyId) =>
  api.patch(`/handover-inspections/${inspectionId}/handover-to`,
    { to_project_company_id: toProjectCompanyId }).then(r => r.data);

export const cancelHandoverInspection = (inspectionId) =>
  api.post(`/handover-inspections/${inspectionId}/cancel`).then(r => r.data);

/** The way an asset gets BACK onto a booked inspection after new scope took it
 *  off. Deliberately a human act in both directions — nothing re-adds an asset
 *  automatically (docs/design/asset-lifecycle.md §1.4.1). */
export const addAssetToHandoverInspection = (inspectionId, elementId) =>
  api.post(`/handover-inspections/${inspectionId}/assets`, { element_id: elementId })
    .then(r => r.data);

/** Refuses the LAST asset with 409 INSPECTION_WOULD_BE_EMPTY — emptying an
 *  inspection is the automatic path's business, and an admin doing it by hand
 *  means to cancel the event and should say so. */
export const removeAssetFromHandoverInspection = (inspectionId, elementId) =>
  api.delete(`/handover-inspections/${inspectionId}/assets/${elementId}`)
    .then(r => r.data);

/** One asset's whole handover story: its inspections, its punch items, and the
 *  evidence hanging off each item. */
export const getAssetHandover = (elementId) =>
  api.get(`/assets/${elementId}/handover`).then(r => r.data);

/**
 * LC9 — HUMAN GATE 2. The client accepts the asset.
 *
 * NO BODY, on purpose: nothing about this act is the caller's to state. Who
 * accepted comes from the session, when comes from the server clock, and
 * whether the asset may be accepted at all comes from the same derivation the
 * screen is displaying. A payload here would be three opportunities to
 * disagree with the record.
 *
 * Refuses with 409 in two shapes, both of which the screen must SAY rather
 * than swallow: `ACCEPTANCE_ALREADY_STANDING` (idempotent — somebody already
 * accepted, and the detail carries who and when) and
 * `ASSET_NOT_READY_FOR_ACCEPTANCE` (the detail names what is in the way).
 */
// HC-5c — `acceptAsset` foi removido com a porta: o servidor responde 410
// (ACCEPTANCE_IS_DERIVED). O aceite é derivado do registro que a inspeção de
// handover produz, por (asset, elo); não há mais escrita manual a chamar.

// --- punch items ----------------------------------------------------------

/** `category` has NO DEFAULT here either, and that is not an oversight to be
 *  tidied: the server's field is a required Literal, so omitting it is a 422.
 *  A default on this side would only move the guess earlier. */
/** PROMPT 104 · ITEM 1 — `responsible_project_company_id` is the LINK, and the
 *  server refuses one that does not belong to the punch item's project (409
 *  RESPONSIBLE_COMPANY_NOT_IN_PROJECT). `responsible` stays in the signature
 *  because the field stays in the record: the modal stopped sending it, which
 *  is not the same as the field ceasing to exist. */
// H-FE-1 — `reason` is the late-item motive the server demands only when the
// asset is already QA Complete; it goes on the wire only when there is one.
export const createPunchItem = (
  { inspection_id, element_id, description, category,
    responsible = null, responsible_project_company_id = null,
    due_date = null, reason = null },
) => api.post('/punch-items', {
  inspection_id, element_id, description, category, responsible,
  responsible_project_company_id, due_date,
  ...(reason ? { reason } : {}),
}).then(r => r.data);

/** THE REGISTER. `status` and `category` are validated server-side against the
 *  closed vocabularies — an unknown value is a 422, not an empty list, because
 *  an empty register reads exactly like "there is nothing to fix". */
// LC12 — the delivery documents (docs/design/asset-lifecycle.md §3).
//
// Issuing is a POST and admin-only; everything else here is a plain GET,
// including the PDF. The client representative is the RECIPIENT of these
// documents and reads every one of them.
// CLEAN-FE-2 — `issueHandoverPackage` and `handoverPackagePdfUrl` left: only
// the modals removed in CLEAN-FE-1 called them.
export const listHandoverPackages = (projectId) =>
  api.get('/handover-packages', { params: { project_id: projectId } })
    .then(r => r.data);

export const getHandoverPackage = (packageId) =>
  api.get(`/handover-packages/${packageId}`).then(r => r.data);

/** The Final Handover Index, plus the pendencies that block closing it — both
 *  from the SAME server-side helper the completion gate refuses with, so the
 *  banner can never offer a "Complete handover" the server would refuse. */
export const getProjectHandoverIndex = (projectId) =>
  api.get(`/projects/${projectId}/handover-index`).then(r => r.data);

export const completeProjectHandover = (projectId) =>
  api.post(`/projects/${projectId}/complete-handover`).then(r => r.data);

/** PUNCH-STAY-1 — ONE punch item, as the register's row:
 *  `{punch_item, project_timezone}`. The asset's fence first; only when it
 *  refuses, the item's window (its responsible company keeps it after a Move
 *  or a package transfer). 404 for anybody else. */
export const getPunchItem = (punchItemId) =>
  api.get(`/punch-items/${punchItemId}`).then(r => r.data);

export const listPunchItems = (projectId, { status, category } = {}) =>
  api.get('/punch-items', {
    params: {
      project_id: projectId,
      ...(status ? { status } : {}),
      ...(category ? { category } : {}),
    },
  }).then(r => r.data);

/** The builder's act. A category-A item with no evidence is refused with 409
 *  EVIDENCE_REQUIRED and nothing is written — the item stays open. */
// H-FE-1 — the company of acceptance records ON BEHALF of the responsible one
// only with a reason (`on_behalf_reason`); sent only when given.
export const closePunchItem = (punchItemId, closureComment, { onBehalfReason = null } = {}) =>
  api.post(`/punch-items/${punchItemId}/close`, {
    closure_comment: closureComment,
    ...(onBehalfReason ? { on_behalf_reason: onBehalfReason } : {}),
  }).then(r => r.data);

export const verifyPunchItem = (punchItemId) =>
  api.post(`/punch-items/${punchItemId}/verify`).then(r => r.data);

// CLEAN-FE-2 — `assignPunchItem` (PROMPT 107 · F2) left: its one caller was the
// punch-item detail modal removed in CLEAN-FE-1.

/** PL-2 — REFUSING A RECTIFICATION, which is where `reopenPunchItem` used to
 *  be. Reopen is gone from this file because the server answers both of its
 *  doors with a 409 now: from `closed` it points here, and from `verified` it
 *  refuses outright (an approved item is history). A helper whose only possible
 *  outcome is an error message is a helper that invites a caller.
 *
 *  `evidence_ids` cites photos already uploaded through the evidence endpoint —
 *  this call moves no bytes. The server stamped them `rejection` and gave them
 *  the current round when they were uploaded, and it validates that both still
 *  hold before it accepts the refusal. */
export const rejectPunchItem = (punchItemId, reason, evidenceIds = []) =>
  api.post(`/punch-items/${punchItemId}/reject`, {
    reason,
    evidence_ids: evidenceIds,
  }).then(r => r.data);

export const recategorizePunchItem = (punchItemId, category, comment) =>
  api.post(`/punch-items/${punchItemId}/recategorize`, { category, comment })
    .then(r => r.data);

/** H-FE-1 — the handing-over (or responsible) company PROPOSES a category;
 *  the company of acceptance decides (`approve` | `reject`, reason owed on
 *  reject); the proposer may withdraw it while it is pending. */
export const proposePunchCategory = (punchItemId, toCategory, reason) =>
  api.post(`/punch-items/${punchItemId}/category-proposals`,
    { to_category: toCategory, reason }).then(r => r.data);

export const decidePunchCategoryProposal = (punchItemId, proposalId, decision, reason = null) =>
  api.post(`/punch-items/${punchItemId}/category-proposals/${proposalId}/decide`, {
    decision, ...(reason ? { reason } : {}),
  }).then(r => r.data);

export const withdrawPunchCategoryProposal = (punchItemId, proposalId) =>
  api.post(`/punch-items/${punchItemId}/category-proposals/${proposalId}/withdraw`)
    .then(r => r.data);

/** Links an EXISTING NCR on the same asset. It does not create one — the NCR
 *  flow already has a door, and raising one from here would duplicate its
 *  numbering, notifications and thread inside an entity that owns none of it. */
export const escalatePunchItem = (punchItemId, ncrId) =>
  api.post(`/punch-items/${punchItemId}/escalate`, { ncr_id: ncrId })
    .then(r => r.data);

/** Closure evidence. Multipart, and only the file — the punch item is in the
 *  path and the asset is stamped server-side FROM the item, never from here. */
export const uploadPunchItemEvidence = (punchItemId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/punch-items/${punchItemId}/evidence`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// Reports
// generateAssetReport removed - POST /reports/asset/{id}/generate (CLEANUP-1c).
// downloadElementReport removed - orphan GET /reports/element/{id}/pdf (PROMPT 54B).

// Stats
export const getDashboardLite = (projectId = null) =>
  api.get('/stats/dashboard-lite', {
    params: projectId ? { project_id: projectId } : {},
  });
// NAV-FE-2 — the project Home in one read (docs/design/home.md §3): the KPI
// row, "Needs you" and one bar per package. Facts only; the copy is the
// screen's (lib/homeView.js).
export const getProjectHome = (projectId) => api.get(`/projects/${projectId}/home`);
// NAV-FE-3a — the project's ITC register (docs/design/itc-register.md §3), the
// ITCs view of the Assets page. Facts only; the row text is lib/assetsView.js.
export const getProjectItcs = (projectId) => api.get(`/projects/${projectId}/itcs`);
// NAV-FE-3b — the project's RFI list (docs/design/rfi-list.md §3): every RFI the
// reader can already open. Facts only; the row text is lib/rfiListView.js.
export const getProjectRfis = (projectId) => api.get(`/projects/${projectId}/rfis`);
// NAV-FE-5 — the shell's search by number (docs/design/search.md §3): up to 20
// hits the reader can already open, ranked by the server. Facts only; the
// grouping and the doors are lib/searchView.js.
export const searchProject = (projectId, q) => api.get(`/projects/${projectId}/search`, { params: { q } });
// LC11 — `getQAProgress` and `getProgressByLotmap` removed with the legacy ITC
// family. They were exported here and called from nowhere in the app; the routes
// behind them counted `itc_versions.business_status`, a vocabulary that was free
// text per project. Live equivalent: `getDashboardLite`, which now counts
// structured ITCs (docs/design/asset-lifecycle.md §4.2).
export const getAssetQATable = (projectId = null) =>
  api.get('/stats/asset-qa-table', {
    params: projectId ? { project_id: projectId } : {},
  });

// Mobile
// NL22-M — `getMobileInspections` is GONE with `GET /mobile/inspections` and
// the mobile page that was its only caller (owner's R3 decision).

// Invitations
export const createInvitation = (data) => api.post('/invitations', data);
export const getInvitations = () => api.get('/invitations');
export const cancelInvitation = (id) => api.delete(`/invitations/${id}`);
export const validateInvitation = (token) => api.get(`/invitations/validate/${token}`);
export const acceptInvitation = (data) => api.post('/invitations/accept', data);

// CO-7.4: Public endpoint that returns invitation + project + project_company context.
// Used by AcceptInvitationPage to show "Joining {Project} as {Company}" before account creation.
// Endpoint: GET /api/invitations/by-token/{token}/context (CO-7.2)
export const getInvitationContext = (token) =>
  api.get(`/invitations/by-token/${encodeURIComponent(token)}/context`).then(r => r.data);


// Admin Users
export const getAdminUsers = (projectId = null) =>
  api.get('/admin/users', {
    params: projectId ? { project_id: projectId } : {},
  });
export const updateAdminUser = (userId, params) => api.put(`/admin/users/${userId}`, null, { params });
export const deleteAdminUser = (userId) => api.delete(`/admin/users/${userId}`);

// ============== Execution Lots ==============
export const getElementLots = (elementId) => api.get(`/elements/${elementId}/lots`);
// Batch: ALL lots of a drawing's assets in one query (lot-map overlay). Same lot
// shape as getElementLots MINUS the per-lot itc/inspection/ncr counts (the map
// only draws shape + lot_code). Collapses the per-asset N+1.
export const getDrawingLots = (drawingId) => api.get(`/drawings/${drawingId}/lots`);
export const createLot = (elementId, data) => api.post(`/elements/${elementId}/lots`, data);
export const getLot = (lotId) => api.get(`/lots/${lotId}`);
// updateLot removed - orphan PUT /lots/{id} (PROMPT 54B).
// updateLotStatus removed - PROMPT 95: the manual lot-status path is demolished
// (the PATCH /lots/{id}/status route no longer exists; state is derived).
export const updateLotShape = (lotId, data) => api.patch(`/lots/${lotId}/shape`, data);
export const deleteLot = (lotId) => api.delete(`/lots/${lotId}`);
export const getLotPin = (lotId) => api.get(`/lots/${lotId}/pin`);
export const getLotContext = (lotId) => api.get(`/lots/${lotId}/context`);
export const getAssetContext = (elementId) => api.get(`/assets/${elementId}/context`);
// ASSET-FE-1 — the asset page v14 in ONE read (ASSET-BE-1; contract:
// docs/design/asset-page.md §3). The tab lists (RFIs, NCRs, photos, punch) stay
// on their own endpoints and load when the tab opens.
export const getAssetOverview = (elementId) => api.get(`/assets/${elementId}/overview`);
// ASSET-PENDING-FE-1 — what this reader may open of the asset (ASSET-PENDING-BE-1,
// owner Q-PEND-1; docs/design/asset-lifecycle.md §7.14), the overview's guard.
// Asked ONLY after the overview answered 404. Answers:
//   {mode: "full"} — the fence admits (the full page);
//   {mode: "reduced", asset: {id, asset_number, name, location},
//    company: {id, name},
//    pending: [{kind: "ncr"|"rfi"|"punch", id, number, title, status, due, link}]}
//     — the company that left the asset's package; NCRs, RFIs, punch, by
//     number; `link` is an app path (/ncrs/<id>, /threads/<id> or /rfis/<id>,
//     /projects/<p>/handover/punch/<id>);
//   404 «Asset not found» — byte-identical to the overview's: nothing pending.
export const getAssetPending = (elementId) => api.get(`/assets/${elementId}/pending`);
// DOSSIER-FE-1 — the asset's Handover Dossier in ONE read (DOSSIER-BE-1;
// contract: docs/design/dossier.md §3).
export const getAssetDossier = (elementId) => api.get(`/assets/${elementId}/dossier`);
// DOSSIER-PDF-1c-FE — the dossier's PDF (DOSSIER-PDF-1a / 1c-BE; contract:
// docs/design/dossier.md §7). The copy is made only when someone asks: the POST
// answers `{id, status, requested_at}` — 202 a new build, 200 this reader's
// live one. The list is this reader's builds without re-reading the dossier
// (`{builds}`, the rows of `downloads.builds`). The download is recorded before
// its first byte (503 = not recorded, nothing sent) and MUST go through the
// axios instance as a Blob: the token lives in memory, an `<a href>` would
// leave without it. The caller reads the name from `Content-Disposition`.
export const requestAssetDossierBuild = (elementId) => api.post(`/assets/${elementId}/dossier/builds`);
export const listAssetDossierBuilds = (elementId) => api.get(`/assets/${elementId}/dossier/builds`);
export const downloadAssetDossierPdf = (elementId, buildId) =>
  api.get(`/assets/${elementId}/dossier/builds/${buildId}/pdf`, { responseType: 'blob' });

// F4.6 — the legacy asset_attachments write/read helpers (getAssetAttachments /
// uploadAssetAttachment / deleteAssetAttachment) were removed: uploads now go to
// the Document Register via uploadAssetControlledDocument (below), and the
// Asset Context reads the union server-side. Existing rows remain in the DB.

// F4.3 — asset-context controlled-document upload. ONE request creates the
// Document Register document AND its initial scope link. scope_id is DERIVED
// server-side from the authorized asset — the client passes scope_type only and
// MUST NOT pass scope_id. Returns
// { source, document_id, document, revision, scope_link, warning }.
export const uploadAssetControlledDocument = (
  assetId,
  { file, title, documentType, scopeType, revisionLabel, idempotencyKey },
) => {
  const fd = new FormData();
  fd.append('title', title);
  fd.append('document_type', documentType);
  fd.append('scope_type', scopeType);
  if (revisionLabel) fd.append('revision_label', revisionLabel);
  fd.append('file', file);
  const headers = { 'Content-Type': 'multipart/form-data' };
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  return api.post(`/assets/${assetId}/controlled-documents`, fd, { headers });
};

// ============== RFIs ==============
export const getAssetRFIs = (assetId) => api.get(`/assets/${assetId}/rfis`);
// data shape: { title, description, lot_id?, notify_user_ids?, notify_external_emails?,
//   addressed_to_project_company_id?, follows_rfi_id? }
export const createAssetRFI = (assetId, data) =>
  api.post(`/assets/${assetId}/rfis`, data);
// RFI-ACTS-FE-1 — the RFI's acts (H3). Each door answers the RFI's row without
// `asset`/`via`, so the page reads the conversation again instead of merging.
export const closeRfi = (rfiId) => api.post(`/rfis/${rfiId}/close`);
export const withdrawRfi = (rfiId, reason) => api.post(`/rfis/${rfiId}/withdraw`, { reason });
export const setRfiAddressee = (rfiId, projectCompanyId) =>
  api.patch(`/rfis/${rfiId}/addressee`, { addressed_to_project_company_id: projectCompanyId });

// ============== ITPs ==============
export const getAssetITPs = (assetId) => api.get(`/assets/${assetId}/itps`);
export const createAssetITP = (assetId, { title, revision, plan_file }) => {
  const fd = new FormData();
  fd.append('title', title);
  if (revision) fd.append('revision', revision);
  fd.append('plan_file', plan_file);
  return api.post(`/assets/${assetId}/itps`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getITP = (itpId) => api.get(`/itps/${itpId}`);
export const patchITP = (itpId, data) => api.patch(`/itps/${itpId}`, data);
export const deleteITP = (itpId) => api.delete(`/itps/${itpId}`);
export const getITPDocuments = (itpId, category) =>
  api.get(`/itps/${itpId}/documents`, { params: category ? { category } : {} });
export const uploadITPDocument = (itpId, { category, title, file }) => {
  const fd = new FormData();
  fd.append('category', category);
  fd.append('title', title);
  fd.append('file', file);
  return api.post(`/itps/${itpId}/documents`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteITPDocument = (itpId, documentId) =>
  api.delete(`/itps/${itpId}/documents/${documentId}`);

// ============================================================================
// Structured ITP Templates (Phase 5.4A / 5.4D) — distinct from the legacy
// per-asset ITP plan files above. These are project-scoped, revisioned
// inspection plan templates with structured line items that later become
// Structured ITC inputs. project_id is path-only (anti-IDOR §3.5).
// ============================================================================

export const listStructuredITPTemplates = (projectId, { include_archived = false } = {}) =>
  api.get(`/projects/${projectId}/itp-templates`, { params: { include_archived } });

export const createStructuredITPTemplate = (projectId, body) =>
  api.post(`/projects/${projectId}/itp-templates`, body);

export const getStructuredITPTemplate = (templateId) =>
  api.get(`/itp-templates/${templateId}`);

export const updateStructuredITPTemplate = (templateId, body) =>
  api.put(`/itp-templates/${templateId}`, body);

export const publishStructuredITPTemplate = (templateId, supersedePrevious = true) =>
  api.post(`/itp-templates/${templateId}/publish`, { supersede_previous: supersedePrevious });

export const duplicateStructuredITPTemplate = (templateId, body = {}) =>
  api.post(`/itp-templates/${templateId}/duplicate`, body);

export const createITPRevision = (templateId, supersedePreviousOnPublish = true) =>
  api.post(`/itp-templates/${templateId}/duplicate`, {
    action: "new_revision",
    supersede_previous_on_publish: supersedePreviousOnPublish,
  });

export const createITPFromTemplate = (templateId) =>
  api.post(`/itp-templates/${templateId}/duplicate`, { action: "new_itp" });

// ITP per-item comments (approver review workflow)
export const getITPItemComments = (templateId, round = null) =>
  api.get(`/itp-templates/${templateId}/comments`, { params: round !== null ? { round } : {} });

export const createITPItemComment = (templateId, itemId, commentText, commentType) =>
  api.post(`/itp-templates/${templateId}/items/${itemId}/comments`, {
    comment_text: commentText, comment_type: commentType,
  });

export const updateITPItemComment = (templateId, itemId, commentId, commentText, commentType) =>
  api.put(`/itp-templates/${templateId}/items/${itemId}/comments/${commentId}`, {
    comment_text: commentText, comment_type: commentType,
  });

export const deleteITPItemComment = (templateId, itemId, commentId) =>
  api.delete(`/itp-templates/${templateId}/items/${itemId}/comments/${commentId}`);

export const resolveITPItemComment = (templateId, itemId, commentId) =>
  api.post(`/itp-templates/${templateId}/items/${itemId}/comments/${commentId}/resolve`, {});

export const addITPItemCommentReply = (templateId, itemId, commentId, replyText) =>
  api.post(`/itp-templates/${templateId}/items/${itemId}/comments/${commentId}/replies`, {
    reply_text: replyText,
  });

export const submitITPTemplateForApproval = (templateId) =>
  api.post(`/itp-templates/${templateId}/submit-for-approval`, {});

export const reviewITPTemplate = (templateId, body) =>
  api.post(`/itp-templates/${templateId}/review`, body);

export const resubmitITPTemplate = (templateId) =>
  api.post(`/itp-templates/${templateId}/resubmit`, {});

// ITP-FE-2 — the ITP page (prototipo-itp-v1; server ITP-BE-2). Every door
// resolves the project from the ITP; none of these bodies carries one.

// The three answers of an endorsing company (D21.2). `comment` is sent only
// when there is one: "Returned" and "Endorsed with comments" may be carried
// by the review notes alone, and the server says 422 NOTES_OR_COMMENT_REQUIRED
// when there is neither.
export const respondToITP = (templateId, { decision, comment, endorsingCompanyId } = {}) => {
  const body = { decision };
  const words = typeof comment === 'string' ? comment.trim() : '';
  if (words) body.comment = words;
  if (endorsingCompanyId) body.endorsing_company_id = endorsingCompanyId;
  return api.post(`/itp-templates/${templateId}/review`, body);
};

// Review notes pinned to a cell (D21.1): `field` is desc | acc | method |
// evidence | A | B | C (a party letter) | doc; `line_item_id` is absent for a
// note on the document.
export const addITPNote = (templateId, { lineItemId, field, text, companyId } = {}) => {
  const body = { field, text };
  if (lineItemId) body.line_item_id = lineItemId;
  if (companyId) body.company_id = companyId;
  return api.post(`/itp-templates/${templateId}/notes`, body);
};

export const replyITPNote = (templateId, noteId, text) =>
  api.post(`/itp-templates/${templateId}/notes/${noteId}/replies`, { text });

export const resolveITPNote = (templateId, noteId) =>
  api.post(`/itp-templates/${templateId}/notes/${noteId}/resolve`, {});

export const reopenITPNote = (templateId, noteId) =>
  api.post(`/itp-templates/${templateId}/notes/${noteId}/reopen`, {});

// "Who is asked" (D21.3): `{company_id: user_id | null}`, merged over what is
// stored; null clears one company's choice.
export const setITPNominees = (templateId, nominees) =>
  api.patch(`/itp-templates/${templateId}/nominees`, { nominees });

// Copy to another package (D21.5): a new draft there.
export const copyITPToPackage = (templateId, qwpId) =>
  api.post(`/itp-templates/${templateId}/copy`, { qwp_id: qwpId });

// T3-B — the preparing company goes ahead after the endorsers' silence, where
// the QMP rule says "Proceed at risk".
export const proceedITPAtRisk = (templateId, reason) =>
  api.post(`/itp-templates/${templateId}/proceed-at-risk`, { reason });

// WHO MAY ENDORSE, BY COMPANY — the list the "Who is asked" select offers.
// The endorser is the company's Authorised Signatory (D23); the served body is
// `{items: [{company_id, company_name, people: [{user_id, name}]}], total,
// authority: "authorised_signatory"}` — every live company, `people` may be [].
export const getITPEndorsers = (projectId) =>
  api.get(`/projects/${projectId}/itp-authority`);

// ============================================================================
// Project Document Register + AI-Assisted ITP generation (PROMPTs 15/16/19/20)
// Consumed by the New-ITP AI dock (PROMPT 21). project_id is path-only.
// ============================================================================

// List the project's documents (each carries a `current_revision` summary with
// its file_url). Optional `type` filter (e.g. exclude/scope by document_type)
// and `q` free-text search (server matches title + document_number).
export const listProjectDocuments = (projectId, { type, q } = {}) => {
  const params = {};
  if (type) params.type = type;
  if (q) params.q = q;
  return api.get(`/projects/${projectId}/documents`, { params });
};

// Create a document + its revision 1 (multipart). Returns { document, revision, warning }.
export const createProjectDocument = (projectId, { file, title, documentType, documentNumber, discipline, revisionLabel, suitability }) => {
  const fd = new FormData();
  fd.append('title', title);
  fd.append('document_type', documentType);
  if (documentNumber) fd.append('document_number', documentNumber);
  if (discipline) fd.append('discipline', discipline);
  if (revisionLabel) fd.append('revision_label', revisionLabel);
  if (suitability) fd.append('suitability', suitability);
  fd.append('file', file);
  return api.post(`/projects/${projectId}/documents`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Document detail + full revision history. Unlike the list (which embeds a
// `current_revision`), this returns `revisions[]` where each carries a derived
// `is_current` — the two shapes are not interchangeable.
export const getProjectDocument = (documentId) =>
  api.get(`/documents/${documentId}`);

// Upload a NEW immutable revision of an existing document (multipart).
// revision_number is server-side (max+1). Returns { document, revision, warning }.
export const createDocumentRevision = (documentId, { file, revisionLabel, suitability }) => {
  const fd = new FormData();
  if (revisionLabel) fd.append('revision_label', revisionLabel);
  if (suitability) fd.append('suitability', suitability);
  fd.append('file', file);
  return api.post(`/documents/${documentId}/revisions`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// p138 — turn a Register document into an Asset Map (drawing), rendered from
// that document's CURRENT immutable revision. The drawing comes back carrying
// source_document_id / source_document_revision_id, so the map's provenance is
// a stored fact.
//
// This is the ONLY creation path that obeys the Register-first rule: bytes
// enter through createProjectDocument above, and the map is made FROM the
// revision. Do NOT reach for POST /drawings + /drawings/{id}/upload here —
// that pair is the side door this call exists to replace.
//
// `document_id` is the PATH; the body carries NO project_id (the server
// resolves the project from the document and the model is extra="forbid", so
// sending one is a 422). Returns the Drawing.
export const createAssetMapFromDocument = (documentId, { name, pageNumber } = {}) =>
  api.post(`/documents/${documentId}/asset-map`, {
    ...(name ? { name } : {}),
    ...(pageNumber ? { page_number: pageNumber } : {}),
  });

// AI-ITP generation job: create → start → poll → result. mode is always
// "new_itp" in this phase (server default).
export const createItpAiGeneration = (projectId, { scopeDescription, sourceRevisionIds }) =>
  api.post(`/projects/${projectId}/itp-ai-generations`, {
    scope_description: scopeDescription,
    source_revision_ids: sourceRevisionIds,
    mode: 'new_itp',
  });

export const startItpAiGeneration = (jobId) =>
  api.post(`/itp-ai-generations/${jobId}/start`, {});

export const getItpAiGeneration = (jobId) =>
  api.get(`/itp-ai-generations/${jobId}`);

export const getItpAiGenerationResult = (jobId) =>
  api.get(`/itp-ai-generations/${jobId}/result`);

export const getNotifications = (limit = 20) =>
  api.get(`/notifications`, { params: { limit } });
export const getUnreadNotificationCount = () =>
  api.get(`/notifications/unread-count`);
export const markNotificationRead = (notificationId) =>
  api.post(`/notifications/${notificationId}/read`, {});
export const markAllNotificationsRead = () =>
  api.post(`/notifications/read-all`, {});

// Structured ITC generation (5.4B.1): lot-scoped, snapshots a published
// ITP template. Body shape: { itp_template_id, title? }. project_id is
// resolved server-side from the lot path; extra="forbid" rejects routing
// fields in the body.
export const createStructuredITC = (lotId, body) =>
  api.post(`/lots/${lotId}/structured-itcs`, body);

// Read a Structured ITC by id. Response is normalised server-side via
// _normalize_structured_itc (5.4D.0b) so pre-5.4D.0a snapshots still
// parse cleanly through StructuredItcResponse.
export const getStructuredITC = (itcId) =>
  api.get(`/structured-itcs/${itcId}`);

// List a lot's structured ITCs (same normalised shape as the read-by-id).
export const listLotStructuredITCs = (lotId) =>
  api.get(`/lots/${lotId}/structured-itcs`);

// Structured ITC execution — evidence and signing
export const uploadItcEvidence = (itcId, itemId, formData) =>
  api.post(`/structured-itcs/${itcId}/items/${itemId}/evidence`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const listItcEvidence = (itcId, itemId) =>
  api.get(`/structured-itcs/${itcId}/items/${itemId}/evidence`);

// T4 C3 / T-ITC-FE F7 — removal is a SOFT delete and carries a reason
// (`{ reason }` in the body); the server refuses it once the line is signed.
export const deleteItcEvidence = (itcId, evidenceId, reason) =>
  api.delete(`/structured-itcs/${itcId}/evidence/${evidenceId}`, { data: { reason } });

export const signItcItem = (itcId, itemId, body) =>
  api.post(`/structured-itcs/${itcId}/items/${itemId}/sign`, body);

// T1-B (B3) / T-ITC-FE F6 — decline to sign ONE column of ONE line. Body
// `{ signoff_position_id, reason }`; nothing changes state, the decline is
// recorded on the line.
export const declineItcItem = (itcId, itemId, body) =>
  api.post(`/structured-itcs/${itcId}/items/${itemId}/decline`, body);

export const listItcSignatures = (itcId) =>
  api.get(`/structured-itcs/${itcId}/signatures`);

// T2.3 / ITC-FE-1 B1 — the Executing Party records that a W position was not
// signed off in time, against ONE booking. Body `{ position_id, booking_id }`;
// who records it and for which company come from the session.
export const recordItcWitnessAbsence = (itcId, itemId, body) =>
  api.post(`/structured-itcs/${itcId}/items/${itemId}/witness-absence`, body);

// ITC-BE-6 / ITC-FE-3 — "Start inspection now" on ONE H or W cell (D18.10).
// Body `{ signoff_position_id }`; who, for which company and against which
// booking come from the session, the snapshot and the line.
export const startItcInspection = (itcId, itemId, body) =>
  api.post(`/structured-itcs/${itcId}/items/${itemId}/inspection-start`, body);

// D13.4 / ITC-FE-1 B3 — name a different company on ONE (line, position), or
// restore the one it replaced: the same door, `kind` decided server-side.
// Body `{ position_id, participation_id, reason }`; the reason is mandatory.
export const setItcLinePartyOverride = (itcId, itemId, body) =>
  api.post(`/structured-itcs/${itcId}/items/${itemId}/party-override`, body);

// Fatia D — §7's exception: change which company holds ONE role on ONE line
// item. Admin only (enforced server-side), refused while that role already
// carries a signature (the §14 boundary), and audited. Body is
// { role, company_id } — extra="forbid", so routing fields are rejected.
export const setItcItemResponsibleCompany = (itcId, itemId, body) =>
  api.patch(`/structured-itcs/${itcId}/items/${itemId}/responsible-company`, body);

// Inspection bookings (5.4B.3). Booking is planning, not a compliance
// mutation, so the POST is deliberately not behind the hold gate.
// Body shape: { scheduled_at, notified_user_ids, comment? } — routing
// fields are rejected server-side (extra="forbid").
export const createItcBooking = (itcId, itemId, body) =>
  api.post(`/structured-itcs/${itcId}/items/${itemId}/bookings`, body);

// Multi-item inspection booking (Fatia 1): ONE booking covering many line
// items across many ITCs of the SAME project. Body shape:
// { targets: [{itc_id, item_id}], scheduled_at, notified_user_ids: [...], comment? }
// project/lot/element/created_by are server-owned (extra="forbid").
export const createMultiItemBooking = (data) =>
  api.post('/inspection-bookings', data);

// My bookings in a project (notified OR creator), soonest first, each
// enriched with control_type and my_company_signature
// {status: awaiting|signed|not_applicable, signed_by_name?}.
export const getMyInspectionBookings = (projectId) =>
  api.get(`/projects/${projectId}/my-inspection-bookings`);

// Project-wide Inspection Register (all bookings, most recent first),
// same company-signature enrichment as the my-bookings GET.
export const listProjectInspectionBookings = (projectId) =>
  api.get(`/projects/${projectId}/inspection-bookings`);

// Reschedule / cancel a MULTI-ITEM booking (A1 + A1.1). These hit the CANONICAL
// /inspection-bookings/{id} endpoints — NOT the legacy /inspections/{id}
// reschedule/cancel above. `id` is the booking id (b.booking_id || b.id).
// Backend returns 409 when the item is fully signed off (read b.locked first to
// disable the button; the 409 is the fail-closed backstop).
export const rescheduleBooking = (id, body) =>
  api.patch(`/inspection-bookings/${id}/reschedule`, body);
export const cancelBooking = (id, body) =>
  api.patch(`/inspection-bookings/${id}/cancel`, body);
// Frente B: fail ONE item (itc_id + item_id + reason all required). Backend stamps
// state.result="failed" (immutable) and 409s if already failed / fully signed.
export const failInspectionBooking = (id, body) =>
  api.patch(`/inspection-bookings/${id}/fail`, body);
// PERF-FE-2: the inspection history of the WHOLE ITC in one read (PERF-BE-2) —
// `{history: {item_id: [attempt, ...]}}`, one key per snapshot line, each list
// the per-line route's rows (both doc shapes, chronological, enriched).
export const getItcInspectionHistory = (itcId) =>
  api.get(`/structured-itcs/${itcId}/inspections`);
// Frente B (re-inspection): create a NEW inspection for a FAILED item. Body carries
// source_itc_id + source_item_id (the failed item) + the normal booking fields.
export const createReInspection = (sourceFailedBookingId, body) =>
  api.post(`/inspection-bookings/${sourceFailedBookingId}/re-inspection`, body);

// ============================================================================
// Communication System (threads, messages, events)
// ============================================================================

export const getThread = (threadId) =>
  api.get(`/threads/${threadId}`).then(r => r.data);

export const getThreadByEntity = (entityType, entityId) =>
  api.get(`/threads/by-entity/${entityType}/${entityId}`).then(r => r.data);

export const getThreadMessages = (threadId, { limit = 50, before } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (before) params.set('before', before);
  return api.get(`/threads/${threadId}/messages?${params.toString()}`).then(r => r.data);
};

export const getThreadEvents = (threadId, { limit = 100, before } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (before) params.set('before', before);
  return api.get(`/threads/${threadId}/events?${params.toString()}`).then(r => r.data);
};

export const postThreadMessage = (threadId, { content, mentioned_user_ids = [], attachment_ids, message_type } = {}) => {
  const payload = { content, mentioned_user_ids };
  if (Array.isArray(attachment_ids) && attachment_ids.length > 0) {
    payload.attachment_ids = attachment_ids;
  }
  // RFI-ACTS-FE-1 — `response` on an RFI's conversation is the answer; a
  // comment's body stays as it was (no key).
  if (message_type) payload.message_type = message_type;
  return api.post(`/threads/${threadId}/messages`, payload).then(r => r.data);
};

export const postThreadView = (threadId) =>
  api.post(`/threads/${threadId}/view`).then(() => true).catch(() => false);

// ============================================================================
// Communication System — Phase 1B — Attachments
// ============================================================================

export const uploadThreadAttachment = (threadId, file, messageId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (messageId) formData.append('message_id', messageId);
  return api.post(`/threads/${threadId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 2 min for up to 25 MB on 4G
  }).then(r => r.data);
};

export const getThreadAttachments = (threadId) =>
  api.get(`/threads/${threadId}/attachments`).then(r => r.data);

export const deleteThreadAttachment = (threadId, attachmentId) =>
  api.delete(`/threads/${threadId}/attachments/${attachmentId}`).then(r => r.data);

// Helper to build the URL for fetching attachment bytes via proxy stream.
// Used directly in <img src> and anchor href, so we mirror the axios baseURL
// (process.env.REACT_APP_API_URL) and re-prepend /api/.
export const getAttachmentFileUrl = (threadId, attachmentId) => {
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  return `${base}/api/threads/${threadId}/attachments/${attachmentId}/file`;
};

// ----- Quality Breakdown (QB-06A: read-only review surface) -----
// The bench (rows / line resolution) is still GET-only here — a write helper for
// it before QB-06B would let a component mutate the bench by accident. The ONE
// write below is the geometry resolution (QBGEO-2h), a different sub-object.
export const getQbGeneration = (generationId) =>
  api.get(`/qb-generations/${generationId}`);

// QB-RES-1 (P108) — record the HUMAN decision on ONE bench row. This is the only
// endpoint that mutates `resolution`, and it can reach nothing else: the body model
// is extra='forbid' and declares exactly `expected_row_version` + `resolution`.
//
//   state           'pending' | 'confirmed' | 'corrected' | 'removed'
//   correctedValues group -> {name, code, discipline}
//                   asset -> {description, discipline, source_reference}
//                   anything else is a 422.
//
// ⚠ `corrected_values` REPLACES the stored map — it is NOT merged server-side. A
// confirm/remove that sends {} therefore ERASES an existing correction, so callers
// must resend the row's current corrected_values whenever they are not editing.
//
// 409 STALE_ROW means someone resolved the row first and `expected_row_version` is
// out of date — refetch and retry. 422 INCOMPLETE_ROW means the row still lacks the
// field its type requires (group -> name, asset -> description).
export const resolveQbRow = (generationId, rowId,
  { expectedRowVersion, state, correctedValues }) =>
  api.patch(`/qb-generations/${generationId}/rows/${rowId}`, {
    expected_row_version: expectedRowVersion,
    resolution: { state, corrected_values: correctedValues || {} },
  });

// p117-2 — MOVE an existing asset row to another group row of the SAME
// generation. Deliberately its OWN route rather than a field on the resolution
// PATCH: `parent_proposal_row_id` is not a business value the reviewer corrects,
// it is the row's place in the bench, and the correction channel refuses
// identity keys on purpose (_qb_validate_corrected_values). The row keeps its
// proposal_row_id, its provenance, its geometry and its source_reference — this
// is a move, never a delete-and-recreate.
//
// 409 STALE_ROW  — someone else touched the row; refetch and retry.
// 409 (frozen)   — the bench or this row is already materialised.
// 422            — the target is not a live group row of this generation.
export const reparentQbRow = (generationId, rowId,
  { expectedRowVersion, newParentProposalRowId }) =>
  api.post(`/qb-generations/${generationId}/rows/${rowId}/reparent`, {
    expected_row_version: expectedRowVersion,
    new_parent_proposal_row_id: newParentProposalRowId,
  });

// QBGEO-2h — record the HUMAN geometry decision for one row. The real route is a
// PATCH (server.py:26706); `body` is the exact QbGeometryResolutionBody:
//   { action: 'accept_proposal' | 'select_candidate' | 'mark_not_located',
//     proposal_id?, note?, evaluation_id? }
// The model is extra='forbid', so ANY extra key is a 422 — do not decorate it.
// `evaluation_id` must be passed EXPLICITLY by the caller, read from the CURRENT
// evaluation it is looking at: that turns a race with a reevaluation into an
// honest 409 instead of a write landing on an evaluation the human never saw.
// Omit it for pre-versioning (v1) geometry, which has no evaluation_id at all.
export const resolveQbGeometry = (generationId, rowId, body) =>
  api.patch(
    `/qb-generations/${generationId}/rows/${rowId}/geometry-resolution`,
    body,
  );

// QBGEO-P4 — the project's quality-breakdown benches, newest first. This is the only
// QB read not addressed by an id the caller must already know, so it is what makes the
// review screens findable. Each entry carries scope_description (from the job that
// produced it, null for a manual bench), status, dates and rows_total/assets_total.
export const listQbGenerations = (projectId) =>
  api.get(`/projects/${projectId}/qb-generations`);

// p131-3 — name a breakdown. `title` is the ONLY field the endpoint accepts
// (QbGenerationPatchBody is extra='forbid'), so this call cannot be decorated
// into a general-purpose generation writer. An empty/whitespace title is not an
// error: the server restores "Untitled Quality Breakdown".
export const renameQbGeneration = (generationId, title) =>
  api.patch(`/qb-generations/${generationId}`, { title });

// overnight-1 — a BLANK bench. The body is empty BY DESIGN (QbGenerationCreateBody
// declares no fields and is extra='forbid'), which is what makes "no HTTP path can
// mint an AI-provenance row" a property of the schema: `candidate`, `origin_type`
// and `provenance` are all 422 at the boundary. Do NOT decorate this call.
// Returns { generation, rows: [] } — the caller navigates to the review screen and
// fills the bench through createQbManualRow below.
export const createQbGeneration = (projectId) =>
  api.post(`/projects/${projectId}/qb-generations`, {});

// overnight-1 — add ONE row a human wrote. origin_type=manual, no provenance, born
// `confirmed` (the human IS the confirmation), and the generation's revision bumps
// in the same transaction.
//
//   rowType   'group' -> {name, code, discipline, description}
//                        (parentProposalRowId MUST be null)
//             'asset' -> {description, discipline, source_reference}
//                        (parentProposalRowId MUST be a group row of THIS generation)
//
// discipline is REQUIRED for both types and is validated against the closed list with
// the same validator (and the same 422 texts) as the create-asset modal and
// materialisation's gate 5. 422 INCOMPLETE_ROW means the type's required business
// field is still empty (group -> name + code + description, asset -> description).
//
// overnight2-2 — a GROUP also answers:
//   422 "Group code must be 2-4 letters A-Z (e.g. PB, DP, FND)" — the canonical
//       message, the same one the Register's own group writer uses;
//   409 {error: 'group_code_conflicts', message, groups[]} — the code belongs to a
//       different group of this project, or to another live group row of this
//       breakdown. Rich, so the screen can name both groups.
//
// 409 with a STRING detail (GENERATION_MATERIALIZED / ROW_MATERIALIZED) means the
// bench is frozen — rows are the record of what was approved and stop moving once
// they are in the Register. Every refusal writes NOTHING.
export const createQbManualRow = (generationId,
  { rowType, parentProposalRowId, name, code, discipline, description, sourceReference }) =>
  api.post(`/qb-generations/${generationId}/rows`, {
    row_type: rowType,
    parent_proposal_row_id: parentProposalRowId || null,
    ...(rowType === 'group'
      ? { name: name ?? null, code: code ?? null, discipline: discipline ?? null,
          description: description ?? null }
      : {
        description: description ?? null,
        discipline: discipline ?? null,
        source_reference: sourceReference ?? null,
      }),
  });

// QBGEO-P4 — the QB AI generation job: create → start → poll → project-to-staging.
// Mirrors the AI-ITP helpers above; `mode` is always "quality_breakdown" here.
export const createQbAiGeneration = (projectId, { scopeDescription, sourceRevisionIds }) =>
  api.post(`/projects/${projectId}/qb-ai-generations`, {
    scope_description: scopeDescription,
    source_revision_ids: sourceRevisionIds,
    mode: 'quality_breakdown',
  });

export const startQbAiGeneration = (jobId) =>
  api.post(`/qb-ai-generations/${jobId}/start`, {});

export const getQbAiGeneration = (jobId) =>
  api.get(`/qb-ai-generations/${jobId}`);

// The QB's extra step, and the one the ITP has no equivalent of: a job that reached
// ready_for_review still has to be PROJECTED into a staging bench. Idempotent by
// source_run_id — a replay returns the existing bench with already_projected=true.
// Returns { already_projected, generation, rows }.
//
// overnight2-1 — `targetGenerationId` APPENDS the run's rows to a bench that is
// already open instead of minting a new one (the assistant now lives in that
// bench's rail). It travels as a QUERY parameter, not in the body, on purpose:
// the body model declares zero fields, and that emptiness is what makes "no
// request can influence what gets projected" a property of the schema. A
// destination is not content — the rows still come from the run.
export const projectQbAiGenerationToStaging = (jobId, targetGenerationId) =>
  api.post(`/qb-ai-generations/${jobId}/project-to-staging`, {}, {
    params: targetGenerationId ? { target_generation_id: targetGenerationId } : undefined,
  });

// QBGEO-P3 — run the DETERMINISTIC geometry ladder over a generation's asset rows.
// No AI, no cost. Returns {generation_id, evaluator_version, summary{…}}. A 409 with
// detail.error === 'substrate_not_ready' carries `missing_revisions[]` and means ZERO
// rows were written — the sources need (re)ingesting first.
export const evaluateQbGeometry = (generationId, { reevaluate = false } = {}) =>
  api.post(`/qb-generations/${generationId}/geometry-evaluation`, null,
    { params: { reevaluate } });

// QBGEO-P3 — queue the AI perception pass (BILLED, runs in the geo_percep worker).
// 200 returns the whole job; 409 with detail.error === 'perception_job_in_flight'
// carries {job_id, status} and is NOT a failure — it means a pass is already running
// and the caller should follow that one instead.
export const startQbGeometryPerception = (generationId, { includeResolved = false } = {}) =>
  api.post(`/qb-generations/${generationId}/geometry-perception`, null,
    { params: { include_resolved: includeResolved } });

// QBGEO-P3 — poll one perception job. `status` is queued|running|completed|failed;
// `summary` appears when it completes. READ-ONLY.
export const getQbGeometryPerceptionJob = (jobId) =>
  api.get(`/qb-geometry-perception-jobs/${jobId}`);

// QB-MAT-1 (P107) — turn an approved bench into REAL Asset Groups and Assets.
// No body: every input is the generation itself, resolved server-side.
// 200 → { generation_id, status, already_materialized, complete,
//         summary: { groups_created, groups_reused, assets_created,
//                    rows_skipped_already_materialized, failures[] } }
// 409 → detail.error is one of: needs_confirmation_pending | incomplete_rows |
//   orphan_rows | groups_missing_code | invalid_discipline | internal_duplicates |
//   register_conflicts | nothing_to_materialize | generation_not_materializable.
// EVERY 409 means ZERO writes happened — it is safe to fix and call again.
// Idempotent: a generation already materialised comes back with
// already_materialized=true and the stored summary, having created nothing.
export const materializeQbGeneration = (generationId) =>
  api.post(`/qb-generations/${generationId}/materialize`, null);

// QBGEO-2g.2 — one page of a Document-Register revision as a PNG (P95 endpoint).
// Fetched as a Blob through the authed `api` instance (cookie + Bearer travel
// automatically) so the JWT never appears in an <img src>. READ-ONLY. Returns
// Promise<Blob>. `scale` is clamped server-side to [0.5, 4.0].
export const renderRevisionPage = (revisionId, pageIndex, scale = 2) =>
  api
    .get(`/document-revisions/${revisionId}/pages/${pageIndex}/render`, {
      params: { scale },
      responseType: 'blob',
    })
    .then((r) => r.data);

// ============================================================================
// Test Register — the ONE external-request (NL8) door the screens still call:
// the engineering request page downloads a result file through it. Every other
// NL8 read and write helper is gone (LAB-FE-2 B7): the register, the request
// page and the portal read `/test-requests` and `/portal` (LAB-BE-1 / -2).
// ============================================================================

// One result file's BYTES.
//
// This MUST go through the axios instance and come back as a Blob. The token is
// held in memory and attached by the request interceptor, so a plain
// `<a href="/api/...">` would leave without it and the download would 401 —
// there is no cookie-only path to rely on for this route. The caller turns the
// blob into an object URL, clicks it, and revokes it.
export const downloadExternalRequestFile = (projectId, requestId, fileId) =>
  api
    .get(`/projects/${projectId}/external-requests/${requestId}/files/${fileId}/download`,
      { responseType: 'blob' })
    .then((r) => r.data);

// LAB-FE-1 — the five NL8 write doors of this family (issue, accept, return,
// reschedule, cancel) answer 410 `TR_DOOR_RETIRED` since LAB-BE-1b (D20.10).
// Their helpers are gone; the acts below go through `/test-requests`.

// ============================================================================
// LAB-FE-1 — THE TEST REQUEST (LAB-BE-1 / LAB-BE-1b). D20.
//
// Every door resolves `project_id` from the PATH. Refusals carry
// `detail.message` (or a plain string for the 422s): the screens print it
// word for word through `serverMessage` and never compose their own.
// ============================================================================

// The register: `{items, counts, lines_requiring_request}`. `bucket` is one of
// all | awaiting_results | awaiting_review | returned | accepted (422 otherwise);
// `counts` and `lines_requiring_request` come back whatever the bucket.
export const getTestRegister = (projectId, { bucket } = {}) =>
  api.get(`/projects/${projectId}/test-requests`, {
    params: bucket && bucket !== 'all' ? { bucket } : {},
  }).then((r) => r.data);

// The request page: the register row + details, submissions, comments,
// history and `actions` (what THIS reader may do).
export const getTestRequest = (projectId, requestId) =>
  api.get(`/projects/${projectId}/test-requests/${requestId}`).then((r) => r.data);

// Issue. The body is `extra="forbid"`: itc_id, item_id, laboratory_id,
// required_date, required_time?, number_of_tests (integer), location?,
// comment_to_lab?, covers?[{itc_id, item_id}], retest_of?, retest_reason?.
// Never the test name, standard or acceptance — the server reads the line.
export const createTestRequest = (projectId, body) =>
  api.post(`/projects/${projectId}/test-requests`, body).then((r) => r.data);

// The WHOLE list of covered lines, replaced at once (the origin must stay).
export const updateTestRequestCovers = (projectId, requestId, covers) =>
  api.patch(`/projects/${projectId}/test-requests/${requestId}/covers`, { covers })
    .then((r) => r.data);

export const rescheduleTestRequest = (projectId, requestId, { required_date, required_time = null }) =>
  api.post(`/projects/${projectId}/test-requests/${requestId}/reschedule`,
    { required_date, required_time }).then((r) => r.data);

export const cancelTestRequest = (projectId, requestId, { reason }) =>
  api.post(`/projects/${projectId}/test-requests/${requestId}/cancel`, { reason })
    .then((r) => r.data);

// One act, two dimensions: `{decision: "accept", result: "pass"|"fail"}` or
// `{decision: "return", reason}`. Each field belongs to one decision only.
export const reviewTestRequest = (projectId, requestId, body) =>
  api.post(`/projects/${projectId}/test-requests/${requestId}/review`, body)
    .then((r) => r.data);

export const addTestRequestComment = (projectId, requestId, { text }) =>
  api.post(`/projects/${projectId}/test-requests/${requestId}/comments`, { text })
    .then((r) => r.data);

// `[{id, name, accredited, contact_count, has_portal_link, derived}]`, by name.
export const listLaboratories = (projectId) =>
  api.get(`/projects/${projectId}/laboratories`).then((r) => r.data);

// Admin / Company Representative. `{name, contacts: [emails], accredited}`.
// Registering IS authorising: the e-mails open the laboratory's portal.
export const createLaboratory = (projectId, body) =>
  api.post(`/projects/${projectId}/laboratories`, body).then((r) => r.data);

// ============================================================================
// LAB-FE-1 — THE LABORATORY PORTAL. A SEPARATE CLIENT, on purpose.
//
// The laboratory has no account: its session is the `X-Portal-Token` header,
// traded for a one-time code. It never carries the engineering Bearer token or
// cookie, and a 401 here must not bounce the laboratory to the app's /login —
// so none of the interceptors above apply. The session token is kept for the
// TAB (`sessionStorage`, LAB-FE-2 B6: it survives a reload, dies with the tab;
// never `localStorage`) by `components/test-register/portalSession.js`.
// ============================================================================

const portalApi = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || ''}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

const portalHeaders = (session) => ({ 'X-Portal-Token': session });

// The link's public face: `{project_name, laboratory_name, requesting_company}`
// (the last may be null). Nothing else.
export const getPortalContext = (linkToken) =>
  portalApi.get(`/portal/${encodeURIComponent(linkToken)}`).then((r) => r.data);

// Always the same `{message}`, whoever asks (P12).
export const requestPortalCode = (linkToken, { email }) =>
  portalApi.post(`/portal/${encodeURIComponent(linkToken)}/request-code`, { email })
    .then((r) => r.data);

// `{token, expires_at}` (8 hours) or 400 "Invalid or expired code.".
export const verifyPortalCode = (linkToken, { email, code }) =>
  portalApi.post(`/portal/${encodeURIComponent(linkToken)}/verify-code`, { email, code })
    .then((r) => r.data);

// The laboratory's own requests; each row carries its `bucket`
// (open | pending | action_required | history).
export const listPortalTestRequests = (session, { bucket } = {}) =>
  portalApi.get('/portal/test-requests', {
    headers: portalHeaders(session),
    params: bucket ? { bucket } : {},
  }).then((r) => r.data);

export const getPortalTestRequest = (session, requestId) =>
  portalApi.get(`/portal/test-requests/${requestId}`, { headers: portalHeaders(session) })
    .then((r) => r.data);

// ONE submission: several files (`files`, repeated) and one optional comment.
export const uploadPortalResult = (session, requestId, { files, comment }) => {
  const form = new FormData();
  (files || []).forEach((f) => form.append('files', f));
  if (comment && comment.trim()) form.append('comment', comment.trim());
  return portalApi.post(`/portal/test-requests/${requestId}/files`, form, {
    headers: { ...portalHeaders(session), 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);
};

export const listPortalComments = (session, requestId) =>
  portalApi.get(`/portal/test-requests/${requestId}/comments`, { headers: portalHeaders(session) })
    .then((r) => r.data);

// Answers `{id, by_name, by, who, at, text}` — the entry `comments[]` lists.
export const addPortalComment = (session, requestId, { text }) =>
  portalApi.post(`/portal/test-requests/${requestId}/comments`, { text },
    { headers: portalHeaders(session) }).then((r) => r.data);

// One result file's bytes, as a Blob (the header cannot ride on an <a href>).
export const downloadPortalFile = (session, requestId, fileId) =>
  portalApi.get(`/portal/test-requests/${requestId}/files/${fileId}/download`, {
    headers: portalHeaders(session), responseType: 'blob',
  }).then((r) => r.data);

// ============================================================================
// FE-7 — ITC LINE BLOCKERS (NL15-B). §2.25-2.29.
//
// A DIFFERENT PREFIX, ON PURPOSE: these live under `/structured-itcs`, not
// under the register's `/projects/{id}/external-requests`. The blocker belongs
// to the ITC line, not to the request that happened to raise it — a line can be
// blocked by hand with no Test Request in sight (§2.28).
//
// `project_id` is resolved SERVER-SIDE from the ITC by `_itc_blocker_fence`
// and is never sent: none of these helpers takes one, which is the shape of the
// guarantee rather than a comment about it.
// ============================================================================

// Every blocker of the DOCUMENT — open AND released, `created_at` ascending.
// It is per-ITC and not per-line: the caller filters by `item_id` and keeps the
// server's order. The released ones stay in the answer because the §2.29
// sentence lives on them, and "the result was never provided, and a closed NCR
// explains why" is precisely what the record has to keep showing.
export const listItcBlockers = (itcId) =>
  api.get(`/structured-itcs/${itcId}/blockers`);

// §2.28 — block a line BY HAND: "this evidence cannot be provided". The body is
// `extra="forbid"` and carries exactly one field; `reason` is required, 1..1000,
// because a blocker with no explanation stops a line without telling anyone what
// to do next, and a year later nobody can reconstruct why it stopped.
// 404 `Item not found in this ITC` when the line is not in the snapshot.
export const createItcItemBlocker = (itcId, itemId, { reason }) =>
  api.post(`/structured-itcs/${itcId}/items/${itemId}/blockers`, { reason });

// §2.26 — link an EXISTING NCR. Several are allowed and ALL must close before
// the line is released.
//
// THE RESPONSE IS THE POINT. The server pushes the link, re-reads the blocker,
// runs the release condition and re-reads AGAIN, so the body that comes back is
// the blocker ALREADY REFRESHED — linking an NCR that is already closed comes
// back `status: "released"` with its label. The caller renders the RESPONSE and
// must never assume the blocker stayed open.
//
// TWO REFUSALS THE SCREEN MUST NOT DRESS UP:
//   · 409 with `detail` as a DICT `{code:"NCR_ALREADY_LINKED", ncr_thread_id}` —
//     it has no `message`, so `apiErrorMessage` falls through to the fallback,
//     which is why this call's fallback must be a whole true sentence.
//   · 404 `"Not found"`, OPAQUE, for an NCR that does not exist, is not an NCR,
//     or belongs to ANOTHER PROJECT — all three answer identically on purpose,
//     since confirming existence would disclose a non-conformity elsewhere. The
//     screen says "not found", never "not allowed".
//
// There is deliberately NO unlink helper and NO create-NCR-from-here helper:
// neither door exists on the server (declared deferrals), and an NCR is born
// through its own flow with its own numbering and notifications.
export const linkNcrToBlocker = (blockerId, { ncr_thread_id }) =>
  api.post(`/structured-itcs/blockers/${blockerId}/ncr-links`, { ncr_thread_id });

// ============================================================================
// FE-Q1 — THE QUALITY MANAGEMENT PLAN (NL18-A/B + NL19).
//
// The plan of a project: what governs now, what is being written, what was
// declared before. `project_id` is a PATH segment on every door and never a
// body field — the server resolves the fence from it.
// ============================================================================

// The WHOLE plan, `min_role="viewer"`: a rule you may not read is not a rule
// you can follow. Returns
//   { project_id,
//     current_revision|null,   // the revision in effect {revision, declared_at, ...}
//     structure[],             // the packages and their parties, one row each
//     packages{qwp_id: {draft, proposed, current, has_changes, needs_review,
//                       governance, approvals[], approvers_if_published[]}},  // the rules, per package
//     standard_rules, standard_options, standard_steps,  // the standard, served
//     signing_statement,       // the words a declaration signs
//     approval_due, approvals_due_at,
//     approvals_summary,       // {revision, outstanding, objected, approved,
//                              //  packages_total, packages_awaiting, packages_approved}
//     can_publish, publish_blockers[],
//     approval_act|null,       // D30 — the one act the reader's company may sign:
//                              //  {revision, participation_id,
//                              //   pending[{qwp_id, rules_hash, structure_version}],
//                              //   can_decide}; null with no participation
//     history[] }              // newest first: {revision, declared_at, declared_by, declared_by_name}
// The rules live per package (`packages[*]`); the answer carries no project-wide
// clause keys (CLEAN-BE-2).
export const getProjectQmp = (projectId) =>
  api.get(`/projects/${projectId}/qmp`);

// DECLARE. Freezes the draft as Revision N and that revision governs from the
// instant it lands. The rules are never in the body: what freezes is what the
// doors wrote, and a body carrying rules would let the declared revision say
// something the draft never said.
//
// QMP-FE-5 — THE BODY IS THE ACKNOWLEDGEMENT OF THE BEFORE YOU SIGN STATEMENT,
// AND ONLY THAT (NL21-A3, D14.9): `{guide_acknowledged: true, guide_version}`.
// Without it the server refuses 422 `GUIDE_ACKNOWLEDGEMENT_REQUIRED`.
//
// 403 `NOT_GOVERNING_COMPANY` / `NOT_QMP_SIGNATORY`; 422 `NEEDS_REVIEW` /
// `RULES_INCOMPLETE` (with `qwp_id`) or "nothing to declare"; 422 `NOTHING_CHANGED`
// (with `revision`); 409 when somebody else just took the same number; 503
// `QMP_UNREADABLE`. All arrive as the server's own words.
export const declareQmp = (projectId, acknowledgement) =>
  api.post(`/projects/${projectId}/qmp/declare`, acknowledgement);

// QMP-FE-2 — SAVE ONE SUBAREA OF STEPS 2-5 (NL21-A2 §6.2): `section` is the
// server token (`itp_rules`, `hold_points`, ...) and `packagesMap` is
// `{qwp_id: explicit value}` for EVERY live package — the "All packages" row
// is never sent (D11.1). The governing company only: 403
// `NOT_GOVERNING_COMPANY`; 422 `{code, message, qwp_id?, field?, reason?}`.
export const saveQmpPackageRules = (projectId, section, packagesMap) =>
  api.put(`/projects/${projectId}/qmp/packages/rules/${section}`,
    { packages: packagesMap });

// QMP-FE-7 — "Restore standard rules", ONE sub-step: `step` is the sub-step code
// (`2a` … `5`). The answer is the save's (`section`, `packages`,
// `needs_review`, `routes_derived`) plus `restored`. 422 `UNKNOWN_SECTION`,
// 403 `NOT_GOVERNING_COMPANY`, and the save's own 422s.
export const restoreQmpStandard = (projectId, step) =>
  api.post(`/projects/${projectId}/qmp/draft/restore-standard`, { step });

// ---------------------------------------------------------------------------
// QMP-FE-6 — STEP 7 "APPROVALS" (NL21-A4, D11.9, D14.9)
// ---------------------------------------------------------------------------

// ONE DECLARED REVISION, WHOLE. `GET /qmp/revisions/{n}` returns the row as it
// was declared plus the DERIVED `diff_from_previous`:
//   {"<qwp_id>": {"sections_changed": ["ai", ...], "previous_rules_hash": str|null}}
// `previous_rules_hash: null` means the revision before did not cover the
// package — it is new, and nothing is struck through (D14.10).
// 404 when the number was never declared; 503 `QMP_UNREADABLE`.
export const getQmpRevision = (projectId, revision) =>
  api.get(`/projects/${projectId}/qmp/revisions/${revision}`);

// ONE COMPANY ANSWERS FOR ONE PACKAGE. The body names the revision it is
// answering (that is what makes the act refusable when the plan moved under
// the page), the decision, and — for an approval — the same acknowledgement of
// the Before you sign statement publishing carries (D14.9); an objection
// carries its reason and no acknowledgement (CLEAN-BE-1 B4):
//   {revision, decision: "approved", guide_acknowledged, guide_version}
//   {revision, decision: "objected", reason}
// 201 -> the record. Refusals, by `detail.code`:
//   403 NOT_REQUIRED_APPROVER | NOT_QMP_SIGNATORY
//   409 REVISION_NOT_CURRENT (carries `revision` or `qwp_id`) | ALREADY_CURRENT
//   422 GUIDE_ACKNOWLEDGEMENT_REQUIRED | REASON_REQUIRED
//   404 (a package of another project) · 503 QMP_UNREADABLE
export const approveQmpPackage = (projectId, qwpId, body) =>
  api.post(`/projects/${projectId}/qmp/packages/${qwpId}/approvals`, body);

// D30 — ONE ACT OVER EVERY PACKAGE WAITING FOR THE COMPANY — all or nothing,
// one record per package, sharing a `batch_id`. The approval body plus
// `qwp_ids`, `scope` (what the page showed, entry for entry:
// `[{qwp_id, rules_hash, structure_version}]`) and `participation_id`.
// 422 `APPROVAL_SCOPE_REQUIRED` without them; 409 `APPROVAL_SCOPE_CHANGED`
// (carries `qwp_ids`, `revision`) when what is pending moved;
// `CHANGES_DIFFER` only for a batch of objections; plus every refusal of the
// single door.
export const approveQmpPackagesBatch = (projectId, body) =>
  api.post(`/projects/${projectId}/qmp/approvals/batch`, body);

// WHO SHOULD BE ASKED — an address, never an authority. The governing company
// proposes for any participation; the participation corrects its own.
// 403 NOT_YOUR_PARTICIPATION · 422 UNKNOWN_PARTICIPATION | NOT_A_MEMBER · 404.
export const setQmpApprovalNominee = (projectId, qwpId, participationId, userId) =>
  api.put(
    `/projects/${projectId}/qmp/packages/${qwpId}/nominees/${participationId}`,
    { user_id: userId },
  );

// QMP-FE-7 — WHAT EACH COMPANY HAS EVER SAID ABOUT ONE PACKAGE (§16). Every
// revision, the newest first:
//   {qwp_id, records: [{revision, participation_id, participation_name, roles[],
//     decision, reason (objections only), user_id, user_name, decided_at, batch_id}]}
// Narrowed to the package's companies and the governing one: outside is 404
// (never 403). 503 `QMP_UNREADABLE`.
export const getQmpPackageApprovals = (projectId, qwpId) =>
  api.get(`/projects/${projectId}/qmp/packages/${qwpId}/approvals`);

// GRID-1c-FE — THE READER'S SAVED VIEWS OF ONE GRID (docs/design/grid.md §3).
// The token's user, never a person in the path: `{ views, current }`. `PUT`
// replaces the whole value; 422 past the grid's limits.
export const getGridPrefs = (gridId) => api.get(`/me/preferences/grids/${gridId}`);
export const putGridPrefs = (gridId, prefs) => api.put(`/me/preferences/grids/${gridId}`, prefs);

export default api;
